/**
 * HouseholdSettings Component
 *
 * Settings section for managing household members and invitations.
 * Provides functionality for:
 * - Renaming household (owner only) - WP-HOUSEHOLD-2
 * - Viewing member list
 * - Inviting new members (owner only)
 * - Removing members (owner only)
 * - Leaving household
 * - Transferring ownership (owner only)
 */

import { useState, useCallback } from 'react';
import { Card, CardHeader, Button, Alert, Spinner } from '@/components/ui';
import { MemberList, InviteModal } from '@/components/household';
import HouseholdNameForm from './HouseholdNameForm';
import {
  useHouseholdMembers,
  useHouseholdInvitations,
  useRemoveMember,
  useLeaveHousehold,
  useTransferOwnership,
  useRevokeInvitation,
  useMyHouseholds,
} from '@/hooks/useHouseholds';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/components/ui/Toaster';
import { cn } from '@/utils/cn';
import type { Invitation, HouseholdRole } from '@/types/household';

/**
 * Confirmation dialog component
 */
interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  confirmVariant = 'danger',
  isLoading,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="alertdialog" aria-modal="true">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl sm:my-8 sm:w-full sm:max-w-md">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="mt-2 text-sm text-gray-600">{message}</p>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
            <Button
              variant={confirmVariant}
              onClick={onConfirm}
              isLoading={isLoading}
              className="w-full sm:w-auto"
            >
              {confirmLabel}
            </Button>
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
              className="mt-3 w-full sm:mt-0 sm:w-auto"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Format invitation status for display
 */
function formatInvitationExpiry(expiresAt: string | undefined | null): string {
  if (!expiresAt) {
    return 'Unknown';
  }

  const date = new Date(expiresAt);
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  const now = new Date();
  const diffMs = date.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'Expired';
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) {
    const diffMins = Math.floor(diffMs / (1000 * 60));
    return `Expires in ${diffMins}m`;
  } else if (diffHours < 24) {
    return `Expires in ${diffHours}h`;
  } else {
    const diffDays = Math.floor(diffHours / 24);
    return `Expires in ${diffDays}d`;
  }
}

export default function HouseholdSettings() {
  const user = useAuthStore((state) => state.user);
  const tenantId = user?.tenantId || '';

  // State
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    type: 'remove' | 'leave' | 'transfer' | 'revoke';
    targetId: string;
    targetName: string | null;
  } | null>(null);

  // Queries
  const { data: membersData, isLoading: membersLoading, error: membersError } =
    useHouseholdMembers(tenantId);
  const { data: invitationsData, isLoading: invitationsLoading } =
    useHouseholdInvitations(tenantId);
  // WP-HOUSEHOLD-2: Get current household name for rename form
  const { data: householdsData } = useMyHouseholds();
  const currentHousehold = householdsData?.households.find((h) => h.id === tenantId);

  // Mutations
  const removeMember = useRemoveMember();
  const leaveHousehold = useLeaveHousehold();
  const transferOwnership = useTransferOwnership();
  const revokeInvitation = useRevokeInvitation();

  // Determine user's role
  const currentMember = membersData?.members.find((m) => m.isCurrentUser);
  const currentUserRole: HouseholdRole = currentMember?.role || 'member';
  const isOwner = currentUserRole === 'owner';
  const hasOtherMembers = (membersData?.count || 0) > 1;

  // Handlers
  const handleRemoveMember = useCallback((userId: string, memberName: string | null) => {
    setConfirmDialog({
      type: 'remove',
      targetId: userId,
      targetName: memberName,
    });
  }, []);

  const handleTransferOwnership = useCallback((userId: string, memberName: string | null) => {
    setConfirmDialog({
      type: 'transfer',
      targetId: userId,
      targetName: memberName,
    });
  }, []);

  const handleLeaveHousehold = useCallback(() => {
    setConfirmDialog({
      type: 'leave',
      targetId: tenantId,
      targetName: null,
    });
  }, [tenantId]);

  const handleRevokeInvitation = useCallback((invitationId: string) => {
    setConfirmDialog({
      type: 'revoke',
      targetId: invitationId,
      targetName: null,
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!confirmDialog) return;

    try {
      switch (confirmDialog.type) {
        case 'remove':
          await removeMember.mutateAsync({
            tenantId,
            userId: confirmDialog.targetId,
          });
          toast.success(`${confirmDialog.targetName || 'Member'} has been removed from the household.`);
          break;

        case 'transfer':
          await transferOwnership.mutateAsync({
            tenantId,
            newOwnerId: confirmDialog.targetId,
          });
          toast.success(`${confirmDialog.targetName || 'Member'} is now the owner of this household.`);
          break;

        case 'leave':
          await leaveHousehold.mutateAsync(tenantId);
          toast.success('You have left the household.');
          // Redirect will happen via auth state change
          break;

        case 'revoke':
          await revokeInvitation.mutateAsync({
            tenantId,
            invitationId: confirmDialog.targetId,
          });
          toast.success('The invitation has been revoked.');
          break;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Operation failed';
      toast.error(message);
    } finally {
      setConfirmDialog(null);
    }
  }, [confirmDialog, tenantId, removeMember, transferOwnership, leaveHousehold, revokeInvitation]);

  // Confirmation dialog messages
  const getConfirmDialogProps = () => {
    if (!confirmDialog) return null;

    switch (confirmDialog.type) {
      case 'remove':
        return {
          title: 'Remove member',
          message: `Are you sure you want to remove ${confirmDialog.targetName || 'this member'} from the household? They will lose access to all shared data.`,
          confirmLabel: 'Remove',
          confirmVariant: 'danger' as const,
        };
      case 'transfer':
        return {
          title: 'Transfer ownership',
          message: `Are you sure you want to make ${confirmDialog.targetName || 'this member'} the owner of this household? You will become a regular member.`,
          confirmLabel: 'Transfer Ownership',
          confirmVariant: 'primary' as const,
        };
      case 'leave':
        return {
          title: 'Leave household',
          message: isOwner && hasOtherMembers
            ? 'You are the owner. Please transfer ownership to another member before leaving.'
            : isOwner
            ? 'You are the only member. Leaving will archive this household. Are you sure?'
            : 'Are you sure you want to leave this household? You will lose access to all shared data.',
          confirmLabel: 'Leave',
          confirmVariant: 'danger' as const,
        };
      case 'revoke':
        return {
          title: 'Revoke invitation',
          message: 'Are you sure you want to revoke this invitation? The recipient will no longer be able to join.',
          confirmLabel: 'Revoke',
          confirmVariant: 'danger' as const,
        };
    }
  };

  if (!tenantId) {
    return (
      <Alert variant="error">
        Unable to load household settings. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* WP-HOUSEHOLD-2: Household Name Card (owner only) */}
      {isOwner && (
        <Card>
          <CardHeader
            title="Household Name"
            description="The name of this household as shown to all members"
          />
          <HouseholdNameForm
            currentName={currentHousehold?.name || ''}
            tenantId={tenantId}
          />
        </Card>
      )}

      {/* Members Card */}
      <Card>
        <CardHeader
          title="Household Members"
          description="People who have access to this household"
          action={
            isOwner && (
              <Button variant="primary" size="sm" onClick={() => setIsInviteModalOpen(true)}>
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Invite
              </Button>
            )
          }
        />

        {membersError && (
          <Alert variant="error" className="mb-4">
            Failed to load members. Please try again.
          </Alert>
        )}

        <MemberList
          members={membersData?.members || []}
          isLoading={membersLoading}
          currentUserRole={currentUserRole}
          onRemoveMember={handleRemoveMember}
          onTransferOwnership={handleTransferOwnership}
        />
      </Card>

      {/* Pending Invitations Card (owner only) */}
      {isOwner && (
        <Card>
          <CardHeader
            title="Pending Invitations"
            description="Invitations that have been sent but not yet accepted"
          />

          {invitationsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : !invitationsData?.invitations.length ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No pending invitations.
            </p>
          ) : (
            <div className="divide-y divide-gray-200">
              {invitationsData.invitations.map((invitation: Invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between py-3"
                >
                  <div className="flex items-center gap-3">
                    {invitation.invitationType === 'email' ? (
                      <>
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-sm text-gray-900">{invitation.email}</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                          />
                        </svg>
                        <code className="text-sm font-mono bg-gray-100 px-2 py-0.5 rounded">
                          {invitation.code}
                        </code>
                      </>
                    )}
                    <span
                      className={cn(
                        'text-xs px-2 py-0.5 rounded',
                        new Date(invitation.expiresAt) <= new Date()
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {formatInvitationExpiry(invitation.expiresAt)}
                    </span>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevokeInvitation(invitation.id)}
                    aria-label="Revoke invitation"
                  >
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Leave Household Card */}
      <Card>
        <CardHeader
          title="Leave Household"
          description={
            isOwner && hasOtherMembers
              ? 'Transfer ownership before leaving'
              : 'Remove yourself from this household'
          }
        />

        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {isOwner && hasOtherMembers
              ? 'You must transfer ownership to another member before you can leave.'
              : isOwner
              ? 'Since you are the only member, leaving will archive the household.'
              : 'You will lose access to all shared data in this household.'}
          </p>
          <Button
            variant="danger"
            size="sm"
            onClick={handleLeaveHousehold}
            disabled={isOwner && hasOtherMembers}
          >
            Leave
          </Button>
        </div>
      </Card>

      {/* Invite Modal */}
      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        tenantId={tenantId}
      />

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          isOpen={!!confirmDialog}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmDialog(null)}
          isLoading={
            removeMember.isPending ||
            leaveHousehold.isPending ||
            transferOwnership.isPending ||
            revokeInvitation.isPending
          }
          {...getConfirmDialogProps()!}
        />
      )}
    </div>
  );
}
